import type { ReactElement } from 'react'
import type { TFunction } from 'i18next'
import { Link } from 'react-router-dom'
import { ArrowRight, FileText } from 'lucide-react'
import type { ReportDomain } from '../../../types'
import { DOMAIN_LABELS } from '../../../types'

export function ReportsLoading(): ReactElement {
  return (
    <div className="flex h-48 items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#4e5e82] border-t-transparent" />
    </div>
  )
}

interface ErrorProps {
  message: string
  retryLabel: string
}

export function ReportsError({ message, retryLabel }: ErrorProps): ReactElement {
  return (
    <div className="flex h-48 flex-col items-center justify-center gap-4 rounded-lg border border-dashed border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/20">
      <p className="text-red-600 dark:text-red-400">{message}</p>
      <button
        onClick={() => {
          window.location.reload()
        }}
        className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-500"
      >
        {retryLabel}
      </button>
    </div>
  )
}

interface EmptyProps {
  domain: ReportDomain
  t: TFunction<'admin'>
}

export function ReportsEmpty({ domain, t }: EmptyProps): ReactElement {
  return (
    <div className="flex h-48 flex-col items-center justify-center rounded-lg border border-dashed border-stone-300 bg-stone-50 dark:border-stone-700 dark:bg-stone-900">
      <FileText className="h-12 w-12 text-stone-300 dark:text-stone-600" />
      <p className="mt-4 text-stone-500 dark:text-stone-400">
        {t('reports.noReportsAvailable', { domain: DOMAIN_LABELS[domain].toLowerCase() })}
      </p>
      <Link
        to="/admin/reports"
        className="mt-2 text-sm font-medium text-[#5c6e94] hover:text-[#4e5e82] dark:text-[#8a9bba]"
      >
        {t('reports.browseAllReports')}
      </Link>
    </div>
  )
}

interface HeaderProps {
  domain: ReportDomain
  t: TFunction<'admin'>
}

export function ReportsHeader({ domain, t }: HeaderProps): ReactElement {
  return (
    <div>
      <h2 className="text-lg font-semibold text-stone-900 dark:text-stone-100">
        {t('reports.domainReportsTitle', { domain: DOMAIN_LABELS[domain] })}
      </h2>
      <p className="mt-1 text-sm text-stone-600 dark:text-stone-400">
        {t('reports.domainReportsDescription', { domain: DOMAIN_LABELS[domain].toLowerCase() })}
      </p>
    </div>
  )
}

interface FooterProps {
  t: TFunction<'admin'>
}

export function ReportsFooter({ t }: FooterProps): ReactElement {
  return (
    <div className="border-t border-stone-200 pt-4 dark:border-stone-800">
      <Link
        to="/admin/reports"
        className="inline-flex items-center gap-2 text-sm font-medium text-[#5c6e94] hover:text-[#4e5e82] dark:text-[#8a9bba]"
      >
        {t('reports.viewAllReports')}
        <ArrowRight className="h-4 w-4" />
      </Link>
    </div>
  )
}
